/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useDispatch, useSelector} from 'react-redux';
import { FADE_IN_ANIMATION } from '../styles/animations';
import { BLACK, COOL_GREY, RED_CREEK, WHITE } from '../styles/colors';
import * as Buttons from '../forms/elements/buttons';
import { MediumText } from '../styles/styles';
import { hasValidPassword } from '../admin/userManagement/validation';
import { EMPTY_USER_AR } from '../admin/userManagement/userAr';
import TooltipView from '../layout/tooltip/TooltipView';
import { doNothing } from '../language/module';
import PasswordField from '../forms/elements/PasswordField';
import { updateUserAction } from '../admin/userManagement/updateUserAction';
import { IAppState } from '../core/redux/IAppState';
import { getError, getLoading } from '../core/redux/api/module';
import { ACTION_TYPE } from '../core/redux/actionType';
import { Loader } from '../forms/elements/loader/Loader';
import {getUsersAction} from '../admin/userManagement/getUsersAction';
import {useAuth0} from '@auth0/auth0-react';
import {redirectUri} from '../index';
import {getUserAction} from '../auth/getUserInfoAction';
import {updatePasswordAction} from '../admin/userManagement/updatePasswordAction';
import { getResource } from '../language/resources/getResource';


const styles = {
  root: (isConfirming?: boolean) => css`
    align-self: center;
    margin-top: 100px;
    width: ${isConfirming ? '560px' : '425px'};
    display: flex;
    flex-direction: column;
    align-items: center;
    ${FADE_IN_ANIMATION};
    border: solid ${COOL_GREY} 1px;
    border-radius: 10px;
    padding: 50px;
    background-color: ${WHITE};
  `,
  inputs: css`
    margin: 30px 0;
    display: flex;
    flex-direction: column;
  `,
  textBox: (error?: boolean) => css`
    width: 400px;
    min-height: 35px;
    padding: 8px;
    margin-bottom: 12px;
    ${MediumText};
    ${error && 'border: 1px solid red;'};
  `,
  buttons: css`
    display: flex;
    justify-content: space-between;
    width: 325px;
  `,
  header: css`
    font-size: 44px;
    font-weight: bold;
    margin-bottom: 40px;
  `,
  subheader: css`
    font-style: italic;
  `,
  toolTipError: css`
    ${FADE_IN_ANIMATION};
    ${MediumText};
    background-color: ${BLACK};
    color: ${WHITE};
    padding: 10px;
    white-space: pre-line;
  `,
  errorMessage: css`
    color: ${RED_CREEK};
    line-height: 30px;
    margin-bottom: 20px;
  `,
  confirmation: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 160px;
  `
};

interface ErrorState {
  pw1Format?: string,
  pw2Format?: string,
  pwMatch?: string,
}

const initialErrors: ErrorState = {
  pw1Format: undefined,
  pw2Format: undefined,
  pwMatch: undefined,
}

enum ErrorType {
  pw1Format = 'pw1Format',
  pw2Format = 'pw2Format',
  pwMatch = 'pwMatch',
}

const ResetUserPassword = () => {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [errors, setErrors] = useState(initialErrors)
  const [submitted, setSubmitted] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const dispatch = useDispatch();
  const { logout } = useAuth0();
  const {isLoading, isApiError, user, selectedResort, authUser, isLoadingUsers, resources} = useSelector((state: IAppState) => ({
    userName: state.auth.username,
    isLoading: !!getLoading(state, state.auth.username!, ACTION_TYPE.UPDATE_USER_REQUEST),
    isApiError: !!getError(state, state.auth.username!, ACTION_TYPE.UPDATE_USER_REQUEST),
    user: state.userState,
    selectedResort: state.selectedResort,
    authUser: state.userState,
    isLoadingUsers: !!getLoading(state, ACTION_TYPE.GET_USER_REQUEST, ACTION_TYPE.GET_USERS_REQUEST),
    resources: state.locale,
    })
  );

  const handleLogout = () => logout({logoutParams: {returnTo: redirectUri} });

  const handlePw1 = (value) =>{
    setPw1(value);
    setErrors(initialErrors)
  }

  const handlePw2 = (value) =>{
    setPw2(value);
    setErrors(initialErrors)
  }

  const clearResetPassword = useCallback(() => {
    if (user) {
      dispatch(updateUserAction({...user, resetPassword: false}))
    }
  },[dispatch, user]);

  useEffect(() => {
    if (!user) {
      dispatch(getUsersAction(selectedResort.resortId))
    }
    if (user && authUser.resetPassword !== user.resetPassword) {
      // Because this page is loaded from the logged-in user and updated through the users state we need to re-fetch the user
      dispatch(getUserAction(user.userId))
    }

  }, [user, authUser, dispatch, selectedResort.resortId]);


  useEffect(
    () => {
      if(submitted && !isLoading && !isApiError && !resetComplete) {
        setResetComplete(true)
      }
    },
    [clearResetPassword, submitted, isLoading, isApiError, resetComplete]
  );

  const handleSubmit = () => {
    if (user) {
      const newErrors = {
        pw1Format: hasValidPassword({...EMPTY_USER_AR, password: pw1}, {validatePassword: true})?.msg || '',
        pw2Format: hasValidPassword({...EMPTY_USER_AR, password: pw2}, {validatePassword: true})?.msg || '',
        pwMatch: pw1 !== pw2 ? getResource(resources, 'RESET_USER_PASSWORD_MISMATCH_ERROR') : '',
      }

      if (newErrors.pw1Format || newErrors.pw2Format || newErrors.pwMatch) {
        setErrors(newErrors);
      } else {
        dispatch(updatePasswordAction(user.userId, pw1));
        setSubmitted(true);
      }
    }
  }

  const handleConfirm = () => {
    clearResetPassword();
  }

  const renderConfirmation = () => (
    isLoadingUsers ? <Loader /> :
    <div css={styles.confirmation}>
      <span css={styles.header}>{getResource(resources, 'RESET_USER_PASSWORD_RESET_COMPLETE')}</span>
      {
        !submitted &&  <Buttons.BigNegative onClick={handleLogout} disabled={submitted}>{getResource(resources, 'RESET_USER_PASSWORD_CANCEL')}</Buttons.BigNegative>
      }
      <Buttons.BigNeutral onClick={handleConfirm}>{getResource(resources, 'RESET_USER_PASSWORD_OK')}</Buttons.BigNeutral>
    </div>
  )

  const renderForm = () => (
    <>
      <span css={styles.header}>{getResource(resources, 'RESET_USER_PASSWORD_PASSWORD_RESET')}</span>
      <span css={styles.subheader}>{getResource(resources, 'RESET_USER_PASSWORD_RESET_INSTRUCTIONS')}</span>

      <form css={styles.inputs}>
        <TooltipView side="right"
                     showTooltip={!!errors[`${ErrorType.pw1Format}`]}
                     floatingContentCss={css`width: 100%;`}
                     tooltipContent={<span css={styles.toolTipError}>{errors.pw1Format}</span>}
                     activateOnHover={false}
                     onClickInside={doNothing}
                     onClickOutside={doNothing}
                     onHoverInside={doNothing}
                     onHoverOutside={doNothing}>
          <PasswordField resources={resources}
                         onPasswordChange={handlePw1}
                         password={pw1}
                         placeholder={getResource(resources, 'RESET_USER_PASSWORD_PASSWORD_PLACEHOLDER')}
                         autofill={false}
                         rootCss={styles.textBox(!!errors[`${ErrorType.pw1Format}`])}
          />
        </TooltipView>

        <TooltipView side="bottom"
                     showTooltip={!!errors[`${ErrorType.pw2Format}`]}
                     floatingContentCss={css`width: 100%;`}
                     tooltipContent={<span css={styles.toolTipError}>{errors.pw2Format}</span>}
                     activateOnHover={false}
                     onClickInside={doNothing}
                     onClickOutside={doNothing}
                     onHoverInside={doNothing}
                     onHoverOutside={doNothing}>
          <PasswordField resources={resources}
                         onPasswordChange={handlePw2}
                         password={pw2}
                         placeholder={getResource(resources, 'RESET_USER_PASSWORD_PASSWORD_PLACEHOLDER')}
                         autofill={false}
                         rootCss={styles.textBox(!!errors[`${ErrorType.pw2Format}`])}
          />
        </TooltipView>
      </form>


      <div css={styles.errorMessage}>
        {
          (errors[ErrorType.pwMatch] && !errors[ErrorType.pw1Format] && !errors[ErrorType.pw2Format]) || isApiError
            ? errors[ErrorType.pwMatch] || getResource(resources, 'RESET_USER_PASSWORD_CONNECTION_ERROR')
            : ""
        }&nbsp;
      </div>

      <div css={styles.buttons}>
        <Buttons.BigNeutral onClick={handleSubmit} disabled={submitted}>{getResource(resources, 'RESET_USER_PASSWORD_SAVE')}</Buttons.BigNeutral>
        <Buttons.BigSecondary onClick={handleLogout} disabled={submitted}>{getResource(resources, 'RESET_USER_PASSWORD_CANCEL')}</Buttons.BigSecondary>
      </div>
    </>
  )

  return (
    <div css={styles.root(resetComplete)}>
      {
        submitted && (isLoading || isLoadingUsers)
        ? <Loader />
        : resetComplete ? renderConfirmation() : renderForm()
      }
    </div>
  );
}

export default ResetUserPassword;
